// Build: 2026-01-31 04:36:18 UTC
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import { 
  Sparkles, 
  Image, 
  Copy, 
  Mail, 
  MessageSquare, 
  Instagram, 
  Facebook,
  Globe,
  ArrowRight,
  CheckCircle,
  Zap,
  FileText,
  Languages,
  BarChart3,
  Users,
  TrendingUp,
  Share2,
  Sofa,
  X,
  ExternalLink,
  Phone,
  Calculator,
  Home as HomeIcon
} from 'lucide-react';

const Home = () => {
  const { t } = useLanguage();
  
  const features = [
    {
      icon: Image,
      title: 'AI Photo Enhancement',
      description: 'Automatically improve brightness, contrast, and clarity of your listing photos with one click.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Sofa,
      title: 'AI Virtual Staging',
      description: 'Transform empty rooms into beautifully furnished spaces. Add furniture to living rooms, bedrooms, kitchens & more.',
      color: 'from-purple-500 to-violet-500',
      badge: 'NEW'
    },
    {
      icon: FileText,
      title: 'MLS Remarks Generator',
      description: 'Professional, MLS-compliant property descriptions generated in seconds, not hours.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Instagram,
      title: 'Social Media Ready',
      description: 'Engaging captions for Instagram and Facebook that drive engagement and leads.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Mail,
      title: 'Email & SMS Marketing',
      description: 'Ready-to-send email campaigns and text messages with compelling copy.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Languages,
      title: 'Bilingual Output',
      description: 'Generate all content in English, Spanish, or both languages simultaneously.',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Copy,
      title: 'One-Click Copy',
      description: 'Copy formatted content directly to your MLS with a single click. Works everywhere.',
      color: 'from-teal-500 to-blue-500'
    },
    {
      icon: BarChart3,
      title: 'Market Data Insights',
      description: 'Real-time market trends, mortgage rates, and price indices to share with your clients.',
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: Share2,
      title: 'Market Update Generator',
      description: 'Create branded market updates for email and social media with your agent profile.',
      color: 'from-rose-500 to-pink-500'
    },
    {
      icon: Users,
      title: 'Contact Management',
      description: 'Manage your client contacts, track follow-ups, and import from CSV for easy marketing campaigns.',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Calculator,
      title: 'Mortgage Calculator',
      description: 'Help clients estimate monthly payments with a full breakdown of principal, interest, taxes, insurance & HOA.',
      color: 'from-amber-500 to-yellow-500',
      badge: 'NEW'
    },
    {
      icon: HomeIcon,
      title: 'Home Affordability Calculator',
      description: 'Show clients exactly how much home they can afford based on income, debts, and lending guidelines.',
      color: 'from-emerald-500 to-green-500',
      badge: 'NEW'
    }
  ];
  
  // Feature Showcase data with actual app screenshots
  const showcaseFeatures = [
    {
      id: 'virtual-staging',
      title: 'AI Virtual Staging',
      description: 'Transform empty rooms into beautifully staged spaces. Our AI adds realistic furniture to living rooms, bedrooms, dining rooms, kitchens, and more.',
      image: '/screenshots/showcase-virtual-staging.png',
      icon: Sofa,
      color: 'from-purple-500 to-violet-500',
      highlights: ['Living rooms', 'Bedrooms', 'Dining rooms', 'Kitchens', 'Bathrooms', 'Home offices']
    },
    {
      id: 'market-data',
      title: 'Real-Time Market Data',
      description: 'Access live mortgage rates, median prices, days on market, and inventory data. Generate AI-powered market updates for your clients.',
      image: '/screenshots/showcase-market-data.png',
      icon: BarChart3,
      color: 'from-amber-500 to-orange-500',
      highlights: ['Live mortgage rates', 'Median prices', 'Days on market', 'Market trends']
    },
    {
      id: 'shareable-listing',
      title: 'Shareable Listing Pages',
      description: 'Create beautiful, branded listing pages with all photos, property details, and your contact information. Share anywhere with a single link.',
      image: '/screenshots/showcase-public-listing.png',
      icon: Share2,
      color: 'from-blue-500 to-cyan-500',
      highlights: ['Your branding', 'Click-to-call', 'Click-to-email', 'Social media links'],
      featured: true
    },
    {
      id: 'agent-profile-sharing',
      title: 'Agent Profile & One-Click Sharing',
      description: 'Build your brand with custom profiles featuring your headshot, logo, and contact info. Share listings instantly with one-click copy.',
      image: '/screenshots/showcase-share-listing.png',
      icon: Users,
      color: 'from-green-500 to-emerald-500',
      highlights: ['Profile photo', 'Company logo', 'One-click sharing', 'Client-ready links']
    },
    {
      id: 'social-marketing',
      title: 'Social Media Marketing',
      description: 'Generate engaging captions for Instagram, Facebook, and more. Ready-to-post content that drives engagement and leads.',
      image: '/screenshots/showcase-social-media.png',
      icon: Instagram,
      color: 'from-pink-500 to-rose-500',
      highlights: ['Instagram captions', 'Facebook posts', 'Hashtag suggestions']
    },
    {
      id: 'calculators',
      title: 'Client Calculators',
      description: 'Help your clients make informed decisions with professional mortgage and affordability calculators. Generate PDF reports to share.',
      image: '/screenshots/showcase-calculators.png',
      icon: Calculator,
      color: 'from-amber-500 to-emerald-500',
      highlights: ['Mortgage payments', 'Home affordability', 'PDF reports', 'DTI analysis'],
      badge: 'NEW'
    }
  ];
  
  // State for lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  const stats = [
    { value: '5 min', label: 'Average time to create full marketing package' },
    { value: '12+', label: 'AI-powered features including calculators' },
    { value: '2', label: 'Languages supported (EN/ES)' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Dark gradient like Cloze */}
      <section className="hero-gradient relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Sparkles size={16} className="text-blue-400" />
              {t('hero_badge')}
            </div>
            
            {/* Main headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold text-white leading-tight mb-6 animate-slide-up">
              {t('hero_title_1')}
              <br />
              <span className="text-gradient">{t('hero_title_2')}</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-10 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              AI-powered virtual staging, photo enhancement, MLS remarks, social media content, email marketing, market data, and client calculators — all in one place.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/signup">
                <Button 
                  size="lg" 
                  className="btn-primary-gradient text-white px-8 py-6 text-lg rounded-full border-0"
                  data-testid="hero-cta-btn"
                >
                  {t('hero_cta')}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 px-8 py-6 text-lg rounded-full"
                  data-testid="hero-pricing-btn"
                >
                  {t('hero_cta_secondary')}
                </Button>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-8 mt-10 text-sm text-white/60 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                {t('hero_trial_note')}
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                {t('hero_no_card')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1.7778' }}>
              <iframe 
                src="https://share.synthesia.io/embeds/videos/ec3515e2-d33a-41c1-9dda-bc360e23085f?autoplay=1&muted=1" 
                loading="eager" 
                title="ListingPro AI Demo Video" 
                allowFullScreen 
                allow="autoplay; encrypted-media; fullscreen;" 
                style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, border: 'none', padding: 0, margin: 0, overflow: 'hidden' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-heading font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works - Simplified */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-blue-600 tracking-wider uppercase">How it works</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-4">
              From upload to <span className="text-gradient">everywhere</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Complete your full marketing package in under 5 minutes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Upload', desc: 'Add your property photos and basic listing information' },
              { step: '02', title: 'Generate', desc: 'AI creates MLS remarks, social posts, emails, and SMS' },
              { step: '03', title: 'Copy & Post', desc: 'One-click copy to MLS, social media, and everywhere else' }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 feature-card h-full">
                  <span className="text-6xl font-heading font-bold text-gray-100 absolute top-6 right-6 group-hover:text-blue-100 transition-colors">
                    {item.step}
                  </span>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                      <Zap className="text-white" size={24} />
                    </div>
                    <h3 className="text-xl font-heading font-bold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-blue-600 tracking-wider uppercase">Features</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-4">
              Everything you need, <span className="text-gradient">nothing you don&apos;t</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              One tool to generate all your listing marketing materials
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className={`border-gray-100 shadow-sm feature-card overflow-hidden group ${feature.badge ? 'ring-2 ring-purple-500/50' : ''}`}
                data-testid={`feature-${index}`}
              >
                <CardContent className="p-6 relative">
                  {feature.badge && (
                    <span className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {feature.badge}
                    </span>
                  )}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcase Section - Screenshots */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-purple-600 tracking-wider uppercase">See It In Action</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-6">
              Powerful features, <span className="text-gradient">beautifully simple</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See exactly what you&apos;ll get when you start using ListingPro AI. Click any screenshot to enlarge.
            </p>
          </div>
          
          {/* Featured Showcase - Shareable Listing */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-1">
              <div className="bg-white rounded-[22px] p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      <Share2 size={14} />
                      Most Popular Feature
                    </div>
                    <h3 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                      Share Listings with <span className="text-gradient">One Link</span>
                    </h3>
                    <p className="text-lg text-muted-foreground mb-6">
                      Create beautiful, branded listing pages that include all your photos, property details, 
                      and most importantly - your contact information front and center.
                    </p>
                    <ul className="space-y-3 mb-8">
                      {[
                        { icon: Phone, text: 'Click-to-call phone number' },
                        { icon: Mail, text: 'Click-to-email contact button' },
                        { icon: Instagram, text: 'Social media profile links' },
                        { icon: Users, text: 'Your photo, logo & branding' }
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <item.icon className="text-blue-600" size={16} />
                          </div>
                          <span className="text-muted-foreground">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/signup">
                      <Button size="lg" className="gap-2 rounded-full">
                        Get Started Free <ArrowRight size={18} />
                      </Button>
                    </Link>
                  </div>
                  <div 
                    className="relative cursor-pointer group"
                    onClick={() => setLightboxImage('/screenshots/showcase-public-listing.png')}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl transform rotate-2 opacity-20" />
                    <div className="relative bg-gray-100 rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                        </div>
                        <div className="flex-1 text-center">
                          <span className="text-xs text-gray-400">listingpro.ai/share/abc123</span>
                        </div>
                      </div>
                      <img 
                        src="/screenshots/showcase-public-listing.png" 
                        alt="Shareable listing page preview"
                        className="w-full aspect-[4/3] object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-800 px-4 py-2 rounded-full font-medium shadow-lg transition-opacity">
                          Click to enlarge
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Other Feature Screenshots Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {showcaseFeatures.filter(f => f.id !== 'shareable-listing').map((feature) => (
              <div 
                key={feature.id}
                className="group cursor-pointer"
                onClick={() => setLightboxImage(feature.image)}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  {/* Screenshot Preview */}
                  <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                    <img 
                      src={feature.image} 
                      alt={`${feature.title} preview`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = `https://placehold.co/800x600/f3f4f6/6b7280?text=${encodeURIComponent(feature.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${feature.color} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                        <feature.icon size={14} />
                        {feature.title}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-white/90 text-gray-800 px-4 py-2 rounded-full font-medium shadow-lg">
                        Click to enlarge
                      </span>
                    </div>
                  </div>
                  {/* Description */}
                  <div className="p-5">
                    <p className="text-muted-foreground text-sm mb-3">{feature.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {feature.highlights.slice(0, 3).map((h, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X size={24} />
          </button>
          <img 
            src={lightboxImage} 
            alt="Feature preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.target.src = 'https://placehold.co/1200x800/1f2937/9ca3af?text=Preview+Not+Available';
            }}
          />
        </div>
      )}

      {/* MLS Copy Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-blue-600 tracking-wider uppercase">MLS Ready</span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-6">
                One-click copy for <span className="text-gradient">any MLS</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                No complex integrations needed. Simply copy your generated MLS remarks and paste 
                directly into your MLS system. Works with any platform.
              </p>
              <ul className="space-y-4">
                {[
                  'MLS-compliant property descriptions',
                  'Fair housing language compliance',
                  'Formatted for easy paste'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="text-green-600" size={14} />
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">MLS Remarks</span>
                <Button variant="outline" size="sm" className="gap-2 rounded-full">
                  <Copy size={14} />
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-muted-foreground leading-relaxed font-mono">
                Welcome to this stunning 4-bedroom, 3-bathroom residence located in the heart of downtown. 
                This meticulously maintained home features an open floor plan with soaring ceilings, 
                abundant natural light, and premium finishes throughout. The gourmet kitchen boasts 
                quartz countertops, stainless steel appliances, and a large center island perfect for 
                entertaining. Step outside to your private backyard oasis...
              </div>
              <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                <span className="text-amber-500">⚠️</span>
                Review for accuracy before submitting to MLS
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Market Data Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-center gap-2 mb-6">
                  <BarChart3 className="text-blue-400" size={24} />
                  <span className="font-semibold">Market Snapshot - Florida</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-white/60 text-sm">Median Home Value</p>
                    <p className="text-2xl font-bold">$425,000</p>
                    <p className="text-green-400 text-sm">+8.2% YoY</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-white/60 text-sm">30-Yr Mortgage Rate</p>
                    <p className="text-2xl font-bold">6.85%</p>
                    <p className="text-white/60 text-sm">National Avg</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-white/60 text-sm">Days on Market</p>
                    <p className="text-2xl font-bold">34</p>
                    <p className="text-white/60 text-sm">Average</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-white/60 text-sm">Inventory</p>
                    <p className="text-2xl font-bold">2.1 mo</p>
                    <p className="text-orange-400 text-sm">Hot Market</p>
                  </div>
                </div>
                
                <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-2">
                  <Share2 size={16} />
                  Generate Market Update
                </Button>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="text-sm font-semibold text-blue-600 tracking-wider uppercase">NEW FEATURE</span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-6">
                Share <span className="text-gradient">market insights</span> with clients
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Access real-time market data and generate branded market updates for your clients. 
                Build trust and stay top-of-mind with professional market reports.
              </p>
              <ul className="space-y-4">
                {[
                  'Real-time market data from FRED & FHFA',
                  'AI-generated market update content',
                  'Email and social media ready',
                  'Your branding included automatically'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="text-green-600" size={14} />
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to save hours on <span className="text-gradient">every listing?</span>
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
            Join real estate agents who are already using ListingPro AI to create 
            professional marketing packages in minutes, not hours.
          </p>
          <Link to="/signup">
            <Button 
              size="lg" 
              className="btn-primary-gradient text-white px-10 py-7 text-lg rounded-full border-0"
              data-testid="cta-signup-btn"
            >
              {t('cta_button')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
          <p className="text-white/50 text-sm mt-6">
            {t('pricing_no_card')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
